import { useState } from 'react'

import Grid from '@mui/material/Grid'

import FilterFromToWeek from '@components/toolbar/FilterFromToWeek'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { isValidISOWeek } from '@util/date-util'

import FilterGridItem from '../../../core/components/toolbar/FilterGridItem'
import { FilterSelectCode } from '../../../core/components/toolbar/FilterSelectCode'
import { FilterSelectString } from '../../../core/components/toolbar/FilterSelectString'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import type { ProjectenPlanningSearchFilters } from '../types/ProjectenPlanningSearch'
import { planningProcesstappen } from '../util/planning-util'

export { PlanningFilters }

type PlanningFiltersProps = {
  filters: ProjectenPlanningSearchFilters
  onChange: (
    filters: ProjectenPlanningSearchFilters,
    removeSort?: boolean,
  ) => void
  filterOptions: {
    aannemers: string[]
    combis: GroupedFilterOption[]
    gebieden: FilterOption[]
    gemeenten: string[]
    labels: FilterOption[]
    processtappen: ProcesstapHoofdleiding[]
    typeWerkTypes: string[]
    verantwoordelijken: GroupedFilterOption[]
  }
}

const PlanningFilters = ({
  filters,
  onChange,
  filterOptions: {
    aannemers,
    combis,
    gebieden,
    gemeenten,
    labels: labelOptions,
    processtappen,
    typeWerkTypes,
    verantwoordelijken,
  },
}: PlanningFiltersProps) => {
  const [localFilters, setLocalFilters] =
    useState<ProjectenPlanningSearchFilters>(filters)
  const [prevFilters, setPrevFilters] = useState(filters)

  if (filters !== prevFilters) {
    setPrevFilters(filters)
    setLocalFilters(filters)
  }

  const {
    aannemer,
    combi,
    fromWeek,
    gebied,
    gemeente,
    labels,
    processtap,
    toWeek,
    typeWerk,
    verantwoordelijke,
  } = localFilters

  const onChangeFilters = (
    filters: Partial<ProjectenPlanningSearchFilters>,
  ) => {
    const updatedFilters = { ...localFilters, ...filters }

    setLocalFilters(updatedFilters)

    const { fromWeek, toWeek } = updatedFilters

    const validWeeks =
      (!fromWeek || isValidISOWeek(fromWeek)) &&
      (!toWeek || isValidISOWeek(toWeek))

    if (validWeeks) {
      onChange(updatedFilters)
    }
  }

  return (
    <Grid spacing={2} container>
      <FilterGridItem>
        <FilterSelectCode
          value={combi}
          options={combis}
          label="Combi"
          filterKey="combi"
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectCode
          multiple
          options={gebieden}
          label="Gebied"
          filterKey="gebied"
          value={gebied}
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={gemeente}
          options={gemeenten}
          label="Gemeente"
          filterKey="gemeente"
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={processtap}
          options={processtappen.filter((p) =>
            planningProcesstappen.includes(p),
          )}
          label="Projectstatus"
          filterKey="processtap"
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={typeWerk}
          options={typeWerkTypes}
          label="Type werk"
          filterKey="typeWerk"
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          value={aannemer}
          options={aannemers}
          label="Aannemer"
          filterKey="aannemer"
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectCode
          value={verantwoordelijke}
          options={verantwoordelijken}
          label="Verantwoordelijke"
          filterKey="verantwoordelijke"
          onFilterChange={onChangeFilters}
          variant="outlined"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectCode
          multiple
          options={labelOptions}
          label="Label"
          filterKey="labels"
          value={labels}
          onFilterChange={onChangeFilters}
          variant="outlined"
          disabled={labelOptions.length === 0}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToWeek
          fromWeek={fromWeek}
          toWeek={toWeek}
          onFilterChange={onChangeFilters}
          label="Gepland"
          variant="outlined"
        />
      </FilterGridItem>
    </Grid>
  )
}
